import React, { useState } from 'react';
import { signUp, confirmSignUp, signIn } from 'aws-amplify/auth';
import { Link, useNavigate } from 'react-router-dom';
import Seo from './marketing/Seo';
import BrandMark from './marketing/BrandMark';
import './Auth.css';

function Signup({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email
          }
        }
      });
      setNeedsConfirmation(true);
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmSignUp({ username: email, confirmationCode });
      await signIn({ username: email, password });
      onLogin();
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  if (needsConfirmation) {
    return (
      <div className="auth-page">
        <Seo
          title="Verify email — 3AM AI Life Journal"
          description="Enter the confirmation code sent to your email to finish creating your 3AM AI Life Journal account."
          path="/signup"
        />
        <div className="auth-page__nav">
          <BrandMark className="auth-page__brand-mark" />
          <Link to="/" className="auth-page__back">
            ← Home
          </Link>
        </div>
        <div className="auth-container">
          <div className="auth-box">
            <h1>Verify your email</h1>
            <p className="auth-subtitle">Enter the code sent to {email}</p>
            <form onSubmit={handleConfirm}>
              <div className="input-group">
                <label htmlFor="confirm-code">Confirmation code</label>
                <input
                  id="confirm-code"
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="one-time-code"
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button type="submit" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Seo
        title="Sign up — 3AM AI Life Journal"
        description="Create your free 3AM AI Life Journal account. Overcome anxiety, reach your goals, track moods, and coach with calm AI."
        path="/signup"
      />
      <div className="auth-page__nav">
        <BrandMark className="auth-page__brand-mark" />
        <Link to="/" className="auth-page__back">
          ← Home
        </Link>
      </div>
      <div className="auth-container">
        <div className="auth-box">
          <h1>Create your account</h1>
          <p className="auth-product">Life Journal by 3AM AI</p>
          <form onSubmit={handleSignup}>
            <div className="input-group">
              <label htmlFor="signup-email">Email address</label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
            <div className="input-group">
              <label htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div className="terms-checkbox-container">
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={loading}
              />
              <label htmlFor="terms-checkbox">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">
                  Terms and Conditions
                </a>
              </label>
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={loading || !termsAccepted}>
              {loading ? 'Creating account...' : 'Continue'}
            </button>
          </form>
          <div className="auth-footer">
            Already have an account?{' '}
            <a
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                navigate('/login');
              }}
            >
              Log in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
